'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { isTimeframeChartTargetLoading, resolveTimeframeChartPresentation, shouldCommitTimeframeTarget, type TimeframeChartSnapshot } from '@/lib/chart/timeframe-chart-presentation';
export interface TimeframeChartPresentation<T> {
  readonly data: T | null;
  readonly windowSecs: number;
  readonly isTargetLoading: boolean;
}
interface TimeframeSnapshotStore<T> {
  getSnapshot: () => TimeframeChartSnapshot<T> | null;
  getRevision: () => number;
  subscribe: (listener: () => void) => () => void;
  commit: (target: TimeframeChartSnapshot<T>) => void;
}
function createTimeframeSnapshotStore<T>(): TimeframeSnapshotStore<T> {
  let snapshot: TimeframeChartSnapshot<T> | null = null;
  let revision = 0;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: (): TimeframeChartSnapshot<T> | null => snapshot,
    getRevision: (): number => revision,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    commit(target: TimeframeChartSnapshot<T>): void {
      const identityChanged = shouldCommitTimeframeTarget(snapshot, target, true);
      // Same-request updates already rendered; retain them without notifying on
      // freshly allocated data, which would cause a render loop.
      snapshot = target;
      if (!identityChanged) return;
      revision += 1;
      for (const listener of listeners) listener();
    }
  };
}

/**
 * Keeps a valid chart mounted while another timeframe of the same rendered
 * series loads, then commits the target data and viewport as one snapshot.
 */
export function useTimeframeChartPresentation(args) {
  const $ = _c(12);
  const {
    seriesKey,
    requestKey,
    data,
    targetReady,
    windowSecs
  } = args;
  const [store] = useState(_temp);
  useSyncExternalStore(store.subscribe, store.getRevision, store.getRevision);
  const committed = store.getSnapshot();
  const target = {
    seriesKey,
    requestKey,
    data,
    windowSecs
  };
  let t0;
  let t1;
  if ($[0] !== data || $[1] !== requestKey || $[2] !== seriesKey || $[3] !== store || $[4] !== targetReady || $[5] !== windowSecs) {
    t0 = () => {
      if (targetReady) {
        store.commit({
          seriesKey,
          requestKey,
          data,
          windowSecs
        });
      }
    };
    t1 = [data, requestKey, seriesKey, store, targetReady, windowSecs];
    $[0] = data;
    $[1] = requestKey;
    $[2] = seriesKey;
    $[3] = store;
    $[4] = targetReady;
    $[5] = windowSecs;
    $[6] = t0;
    $[7] = t1;
  } else {
    t0 = $[6];
    t1 = $[7];
  }
  useLayoutEffect(t0, t1);
  const presentation = resolveTimeframeChartPresentation(committed, target, targetReady);
  const t2 = presentation?.data ?? null;
  const t3 = presentation?.windowSecs ?? windowSecs;
  const t4 = isTimeframeChartTargetLoading(presentation, target, targetReady);
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      data: t2,
      windowSecs: t3,
      isTargetLoading: t4
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp() {
  return createTimeframeSnapshotStore();
}